import i18n from '@/utils/i18n';
import MinusIcon from '@assets/icons/minus.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  PixelRatio,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { ANIMATED_OPACITY_DURATION_MS } from '@/constants';
import { APP_CONTAINER_ROUTES, CHECK_IN_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import {
  BORDER_RADIUS_SMALL,
  BORDER_RADIUS_TINY,
  FONTS,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { CheckInFilters, CheckInItem } from '@/contexts/check-in-context-types';
import { createStyles } from '@/contexts/theme-context';
import {
  DECREMENT_HIT_SLOP,
  INCREMENT_HIT_SLOP,
} from '@/components/quantity-selector';
import { OrderCheckInIssuesList } from '@/components/order-check-in/order-check-in-issues-list';
import { OrderCheckInItemContent } from './order-check-in-item-content';
import { StackNavigationProp } from '@react-navigation/stack';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigation } from '@react-navigation/native';

interface Props {
  checkInItem: CheckInItem;
  onItemQuantityUpdate: (itemUUID: string, quantity: number) => void;
  quantity: number;
  saveCheckInChanges: () => Promise<void>;
  selectedFilter: CheckInFilters;
}

type NavigationType = StackNavigationProp<
  AppModalStackParams,
  APP_CONTAINER_ROUTES.OrderCheckIn
>;

const fontScale = PixelRatio.getFontScale();
const ICON_SIZE = 16 * fontScale;
const UPDATE_QUANTITY_BUTTON_SIZE = 32 * fontScale;

export const OrderCheckInListItem: React.FC<Props> = ({
  checkInItem,
  selectedFilter,
  quantity,
  saveCheckInChanges,
  onItemQuantityUpdate,
}) => {
  const [styles, theme] = useStyles();
  const [isFadingOut, setIsFadingOut] = useState(false);
  const opacityValue = useRef(new Animated.Value(1));
  const { navigate } = useNavigation<NavigationType>();

  const { amountOfIssues, checkInItemUUID, checkInUUID, issues, shipQuantity } =
    checkInItem;

  useEffect(() => {
    if (isFadingOut) {
      Animated.timing(opacityValue.current, {
        duration: ANIMATED_OPACITY_DURATION_MS,
        toValue: 0,
        useNativeDriver: true,
      }).start();
    }
  }, [isFadingOut]);

  const debouncedQuantityUpdate = useDebouncedCallback(
    (id: string, quantity: number) => {
      setIsFadingOut(false);
      onItemQuantityUpdate(id, quantity);
    },
    ANIMATED_OPACITY_DURATION_MS,
  );

  const allowAnimationOnSelectedFilter =
    selectedFilter === CheckInFilters.PARTIAL_OR_MISSING ||
    selectedFilter === CheckInFilters.FULLY_RECEIVED;

  const incrementQuantity = () => {
    if (shipQuantity - 1 === quantity && allowAnimationOnSelectedFilter) {
      setIsFadingOut(true);
      debouncedQuantityUpdate(checkInItemUUID, quantity + 1);
    } else {
      onItemQuantityUpdate(checkInItemUUID, quantity + 1);
    }
  };
  const decrementQuantity = () => {
    if (quantity === shipQuantity && allowAnimationOnSelectedFilter) {
      setIsFadingOut(true);
      debouncedQuantityUpdate(checkInItemUUID, quantity - 1);
    } else {
      onItemQuantityUpdate(checkInItemUUID, quantity - 1);
    }
  };

  const reportAnIssue = async () => {
    await saveCheckInChanges();
    navigate(CHECK_IN_ROUTES.CheckInReportAnIssue, {
      checkInItem,
    });
  };

  const progressBarWidth = useMemo(
    () => (quantity / shipQuantity) * 100,
    [quantity, shipQuantity],
  );

  const reportIssueDisabled = shipQuantity === amountOfIssues;

  const progressBar =
    shipQuantity > 0 ? (
      <View
        style={styles.progressBarOuter}
        testID={`progress-bar-${progressBarWidth}%`}
      >
        <View
          style={[
            styles.progressBarInner,
            {
              backgroundColor: issues?.length
                ? theme.sk.color.icon.warning.secondary
                : theme.sk.color.bg.highlight.primary,
              width: `${progressBarWidth}%`,
            },
          ]}
        />
      </View>
    ) : null;

  const mainComponent = (
    <View style={styles.listItemWrapper}>
      <View style={styles.checkInItemWrapper}>
        <View style={styles.row}>
          <OrderCheckInItemContent checkInItem={checkInItem} />
          {progressBar}
        </View>
        {shipQuantity > 0 ? (
          <View style={styles.bottomRow}>
            <Button
              disabled={reportIssueDisabled}
              onPress={reportAnIssue}
              size="small"
              style={[
                styles.reportAnIssue,
                { height: UPDATE_QUANTITY_BUTTON_SIZE },
              ]}
              textStyle={
                reportIssueDisabled
                  ? styles.reportAnIssueLabelDisabled
                  : styles.reportAnIssueLabel
              }
              trackingData={{
                name: 'check_in_report_an_issue',
                properties: {
                  checkInItemUUID: checkInItemUUID,
                },
              }}
              type="tertiary"
            >
              {i18n.t('check_in.report_an_issue')}
            </Button>
            <View style={styles.quantitySelector}>
              <TouchableOpacity
                disabled={quantity === 0 || quantity === amountOfIssues}
                focusable={false}
                hitSlop={DECREMENT_HIT_SLOP}
                onPress={decrementQuantity}
                style={[
                  styles.quantityButton,
                  quantity === 0 || quantity === amountOfIssues
                    ? styles.quantityButtonDisabled
                    : null,
                ]}
                testID="decrement-quantity"
              >
                <MinusIcon
                  fill={theme.colors.accentText}
                  height={ICON_SIZE}
                  testID="minus-icon"
                  width={ICON_SIZE}
                />
              </TouchableOpacity>
              <Text
                style={[TYPOGRAPHY.h3]}
              >{`${quantity}/${shipQuantity}`}</Text>
              <TouchableOpacity
                disabled={quantity === shipQuantity}
                focusable={false}
                hitSlop={INCREMENT_HIT_SLOP}
                onPress={incrementQuantity}
                style={[
                  styles.quantityButton,
                  quantity === shipQuantity
                    ? styles.quantityButtonDisabled
                    : null,
                ]}
                testID="increment-quantity"
              >
                <PlusIcon
                  fill={theme.colors.accentText}
                  height={ICON_SIZE}
                  width={ICON_SIZE}
                />
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </View>
      <OrderCheckInIssuesList
        checkInItemUUID={checkInItemUUID}
        checkInUUID={checkInUUID}
        issues={issues}
      />
    </View>
  );

  return isFadingOut ? (
    <Animated.View
      style={[styles.flexContainer, { opacity: opacityValue.current }]}
    >
      {mainComponent}
    </Animated.View>
  ) : (
    mainComponent
  );
};

const useStyles = createStyles((theme) => ({
  bottomRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  checkInItemWrapper: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
    flex: 1,
    gap: 16,
    padding: SCREEN_PADDING_SMALL,
  },
  flexContainer: {
    flex: 1,
  },
  listItemWrapper: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: BORDER_RADIUS_SMALL,
    flex: 1,
  },
  progressBarInner: {
    borderRadius: BORDER_RADIUS_TINY,
    height: 3,
  },
  progressBarOuter: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
    borderRadius: BORDER_RADIUS_TINY,
    height: 3,
    width: 40,
  },
  quantityButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.accent,
    borderRadius: UPDATE_QUANTITY_BUTTON_SIZE,
    height: UPDATE_QUANTITY_BUTTON_SIZE,
    justifyContent: 'center',
    width: UPDATE_QUANTITY_BUTTON_SIZE,
  },
  quantityButtonDisabled: {
    opacity: 0.2,
  },
  quantitySelector: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  reportAnIssue: {
    paddingHorizontal: 4,
  },
  reportAnIssueLabel: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.tertiary,
    fontSize: theme.fontSizes.small,
  },
  reportAnIssueLabelDisabled: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.disabled,
    fontSize: theme.fontSizes.small,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
  },
}));
